"use client";

import { useRef, type ReactNode } from "react";
import { depthPreviewFor } from "@/lib/depth-preview";
import { trackOwnedAnalyticsOnly } from "@/lib/analytics-events";

/**
 * The `gate` of an OfferPlacement — the shared "paywall-the-depth" surface for
 * every report SKU. Only OfferPlacement renders it.
 *
 * Renders a blurred/masked PREVIEW of the paid report's sections (a description of
 * what's locked, never the real computed content — see depth-preview.ts) directly above
 * the unlock CTA, so the seeker sees the depth EXISTS without it being given away.
 *
 * Contract (verified against the offer guards):
 *  - It does NOT fire an impression — the surface keeps its existing <OfferImpression> with its
 *    byte-identical presentationKey (rules 46/47). No re-fire risk.
 *  - The unlock CTA is passed as {children} — the gate never renders its OWN checkout, so
 *    the surface stays single-decision (rule 39): one CTA, intent-matched via rule-40 policy.
 *  - The masked preview is a TAP-TO-UNLOCK affordance (rule 32): the locked rows look tappable,
 *    so a tap must be a real action, not a dead click. It carries a role=button that scrolls to
 *    + focuses the single {children} CTA — it opens no second checkout, so it is not a competing
 *    offer; it routes the seeker's "I want this depth" intent to the one decision below.
 *  - NET-ZERO on the free tier: the host tool keeps rendering its existing free sections; this
 *    only adds a teaser of paid-only depth. No currently-free content is removed or revealed.
 *  - Graceful: no preview config for this planId → render {children} plainly (no wall).
 *  - `tapFollowsCta` (collect_inputs bridges): the single CTA is a NAVIGATION link to the
 *    birth-details form, so scrolling to it left the tap one step short. October 2026: 122
 *    taps from 83 seekers on calculator bridges, 0 reached checkout. With this flag the tap
 *    activates that same link (same `report_input_collection_started` attribution as a
 *    direct click) and the hint names the real next step instead of promising an unlock.
 *
 * Mobile (rules 58/62): the lock uses a STATIC CSS mask + one-shot blur (`.depth-locked`),
 * never a live `backdrop-filter` or animated filter (mobile-INP killer); the preview region
 * carries reserved intrinsic height so the reveal/mount causes no post-tap CLS.
 */
export default function DepthGate({
  planId,
  language,
  lockedTitle,
  tapFollowsCta = false,
  children,
}: {
  planId: string;
  language: string;
  /** small heading above the locked preview, e.g. "Your full reading also unlocks" */
  lockedTitle?: string;
  /** Bridge surfaces: a tap follows the CTA link instead of only scrolling to it. */
  tapFollowsCta?: boolean;
  /** the single unlock CTA (the existing intent-matched PaidReportCTA card) */
  children: ReactNode;
}) {
  const preview = depthPreviewFor(planId, language);
  const ctaRef = useRef<HTMLDivElement>(null);
  if (!preview) return <>{children}</>;

  const title =
    lockedTitle ??
    (language === "hi" ? "आपकी पूरी रिपोर्ट में खुलता है" : "Your full reading unlocks");
  const teaser =
    language === "hi"
      ? "पूरी रिपोर्ट में यह भाग आपकी जन्म-कुंडली, दशा और गोचर से निजी रूप से खुलता है।"
      : "The full report personalizes this section from your birth chart, dasha, and transits.";
  const unlockHint = tapFollowsCta
    ? language === "hi"
      ? "जन्म विवरण भरने के लिए टैप करें"
      : "Tap to enter birth details"
    : language === "hi"
      ? "खोलने के लिए टैप करें"
      : "Tap to unlock";
  const unlockLabel = tapFollowsCta
    ? language === "hi"
      ? "जन्म विवरण भरें"
      : "Enter birth details"
    : language === "hi"
      ? "पूरी रिपोर्ट खोलें"
      : "Unlock the full report";

  // A tap on the masked preview means "I want this depth" — the strongest possible
  // intent signal. Route it to the ONE checkout below instead of letting it die as
  // a dead click (rule 32: the locked rows LOOK tappable, so make the tap real).
  function jumpToCheckout() {
    const el = ctaRef.current;
    if (!el) return;
    const ctaLink = tapFollowsCta ? el.querySelector<HTMLAnchorElement>("a[href]") : null;
    if (ctaLink) {
      trackOwnedAnalyticsOnly("depth_paywall_unlock_tap", {
        plan_id: planId,
        unlock_action: "follow_cta",
      });
      // The link's own onClick fires the navigation event and routes to the form.
      ctaLink.click();
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const focusable = el.querySelector<HTMLElement>("input, button, [href], [tabindex]");
    // Focus after the smooth scroll settles so mobile keyboards don't fight it.
    window.setTimeout(() => focusable?.focus({ preventScroll: true }), 350);
    // Measure the dead-click→intent conversion (rule 34 owned beacon): a tap on the
    // locked preview used to be a dead click; now it's a signal we can attribute to
    // this element + plan, so we can tell whether the fix actually lifts intent.
    trackOwnedAnalyticsOnly("depth_paywall_unlock_tap", {
      plan_id: planId,
      unlock_action: "scroll_to_cta",
    });
  }

  return (
    <div className="space-y-4">
      <div
        className="depth-locked"
        role="button"
        tabIndex={0}
        aria-label={unlockLabel}
        style={{ cursor: "pointer" }}
        onClick={jumpToCheckout}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            jumpToCheckout();
          }
        }}
      >
        <p className="depth-locked__title">{title}</p>
        <ul className="depth-locked__list">
          {preview.sections.map((s) => (
            <li key={s.label} className="depth-locked__item">
              <p className="depth-locked__label">
                <span className="depth-locked__lock" aria-hidden="true">
                  🔒
                </span>
                {s.label}
              </p>
              {/* shared paid-only depth description, visually masked — reveals nothing real */}
              <p className="depth-locked__teaser">{teaser}</p>
            </li>
          ))}
        </ul>
        <p className="depth-locked__label" style={{ marginTop: 8, opacity: 0.8 }}>
          {unlockHint} {tapFollowsCta ? "→" : "↓"}
        </p>
      </div>
      <div ref={ctaRef}>{children}</div>
    </div>
  );
}
